import { GameMap } from "./game-map";
import { idFromState, Player } from "./player";

/**
 * GameState manages players and map state on the client.
 * - No local player is rendered at startup.
 * - Local player id can be assigned later (e.g., after "Go Online").
 */
export class GameState {
  players: Map<number, Player> = new Map();
  localPlayerId: number = -1;
  map: GameMap;

  get localPlayer(): Player | undefined {
    return this.players.get(this.localPlayerId);
  }

  constructor(
    mapElement: HTMLDivElement,
    private playerLayer: HTMLDivElement,
    localPlayerId?: number,
  ) {
    if (typeof localPlayerId === "number") {
      this.localPlayerId = localPlayerId;
    }
    // Initialize map without centering on a player (no player yet)
    this.map = new GameMap(mapElement, 0, 0);
  }

  setLocalPlayerId(id: number): void {
    this.localPlayerId = id;
    // Optionally update map transform if state already exists
    this.updateMapTransform();
  }

  addPlayer(state: bigint): Player {
    const id = idFromState(state);
    const player = new Player(state, this.playerLayer);
    this.players.set(id, player);
    return player;
  }

  removePlayer(state: bigint): void {
    const id = idFromState(state);
    this.removePlayerById(id);
  }

  removePlayerById(id: number): void {
    const player = this.players.get(id);
    if (!player) return;
    this.playerLayer.removeChild(player.element);
    this.players.delete(id);
  }

  updatePlayer(state: bigint): void {
    const id = idFromState(state);
    const player = this.players.get(id) || this.addPlayer(state);
    const isLocalPlayer = id === this.localPlayerId;
    const prevPlayerState = player.state;

    if (player) {
      player.setState(state);
    } else {
      this.addPlayer(state);
    }

    if (isLocalPlayer && prevPlayerState !== state) {
      this.updateMapTransform();
    }
  }

  updateMapTransform(): void {
    const player = this.localPlayer;
    if (!player) return;
    this.map.updatePan(player.x, player.y);
  }

  bulkSyncState(bulkState: bigint[]): void {
    requestAnimationFrame(() => {
      bulkState.forEach((state) => {
        this.updatePlayer(state);
      });
    });
  }

  fullSync(fullState: bigint[]): void {
    requestAnimationFrame(() => {
      fullState.forEach((state) => {
        this.updatePlayer(state);
      });
      const allIds = new Set(fullState.map(idFromState));
      for (const [k] of this.players) {
        if (!allIds.has(k)) {
          this.removePlayerById(k);
        }
      }
    });
  }
}
